/**
 * Pre-auth tenant identification from a hostname.
 *
 * `orgSlug` in the login/resend-verification bodies is the primary way a caller names its tenant
 * (see auth.contract.ts's doc comment on `orgSlugSchema`). Once a company has its own subdomain —
 * `acme.<appDomain>` — the hostname the request already arrived on names the same tenant, so
 * requiring it a second time in the body is friction, not safety: a hostname-derived slug is
 * validated against the database exactly like a typed one (`resolveOrgBySlug`), so it carries the
 * same trust level. This module only extracts the candidate slug; it decides nothing about whether
 * that org exists.
 */

/**
 * Subdomains the platform reserves for itself, so a company can never register a slug that
 * collides with one (`admin.<appDomain>`, `api.<appDomain>`, ...).
 */
export const RESERVED_SUBDOMAINS = new Set(['www', 'admin', 'api', 'ws', 'app', 'status', 'mail', 'manage']);

/** Mirrors `orgSlugSchema`'s regex in auth.contract.ts — kept independent to avoid a cross-import
 *  between this module and the contract that also uses it (see the `.refine()` there). */
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Extract a tenant slug from a request/browser hostname, or `null` if the host names no tenant —
 * the bare app domain, a reserved platform subdomain, an unrelated domain, or a malformed one.
 *
 * `host` may include a port (`acme.lvh.me:5173`); it is stripped before comparison so this works
 * the same for `req.hostname` (Express, already port-free) and `window.location.hostname`/
 * `window.location.host` (either form) without the caller needing to know which.
 */
export function extractOrgSlugFromHost(
  host: string | null | undefined,
  appDomain: string,
): string | null {
  if (!host || !appDomain) return null;

  const hostname = host.split(':')[0]!.trim().toLowerCase();
  const domain = appDomain.trim().toLowerCase();
  if (!hostname || hostname === domain) return null;

  const suffix = `.${domain}`;
  if (!hostname.endsWith(suffix)) return null;

  const label = hostname.slice(0, -suffix.length);
  if (!SLUG_PATTERN.test(label) || RESERVED_SUBDOMAINS.has(label)) return null;

  return label;
}
